import { Trans } from "@lingui/macro";
import cx from "classnames";
import { useMemo } from "react";
import Skeleton from "react-loading-skeleton";

import { USD_DECIMALS } from "config/factors";
import { selectChainId } from "context/SyntheticsStateContext/selectors/globalSelectors";
import {
  selectTradeboxMarketAddress,
  selectTradeboxMarketInfo,
} from "context/SyntheticsStateContext/selectors/tradeboxSelectors";
import { useSelector } from "context/SyntheticsStateContext/utils";
import { useTradeHistory } from "domain/synthetics/tradeHistory";
import { Token } from "domain/tokens";
import { useGetTrades } from "lib/apis/markets";
import { formatAmountHuman, formatUsd } from "lib/numbers";
import { PositionTradeAction, TradeActionType } from "sdk/types/tradeHistory";

import { TableListSkeleton } from "components/Skeleton/Skeleton";
import { TableTd, TableTh, TableTheadTr, TableTr } from "components/Table/Table";
import { TableScrollFadeContainer } from "components/TableScrollFade/TableScrollFade";

import { formatTradeTime, getTradeHistorySide } from "./utils";
import { useChartHeaderFormattedValues } from "../../useChartHeaderFormattedValues";

import "./Trades.scss";

const TRADE_PAGE_SIZE = 50;
const MARKET_TRADE_LIMIT = 13;

const Trades = ({ selectedToken }: { selectedToken: Token }) => {
  const chainId = useSelector(selectChainId);
  const marketInfo = useSelector(selectTradeboxMarketInfo);
  const marketAddress = useSelector(selectTradeboxMarketAddress) ?? marketInfo?.marketTokenAddress;

  const { avgPrice } = useChartHeaderFormattedValues();
  const currencySymbol = useMemo(() => {
    if (typeof avgPrice === "string") {
      const match = avgPrice.match(/^[^0-9.,]+/);
      if (match) return match[0];
    }
    return "$";
  }, [avgPrice]);

  const { data: marketTradesForSelectedMarket = [], isPending: isSelectedMarketTradesLoading } = useGetTrades(
    selectedToken.symbol,
    {
      limit: MARKET_TRADE_LIMIT,
      chainId,
      marketAddress,
    },
    {
      refetchInterval: 1000,
    }
  );

  const { data: aggregateMarketTrades = [], isPending: isAggregateMarketTradesLoading } = useGetTrades(
    selectedToken.symbol,
    {
      limit: MARKET_TRADE_LIMIT,
      chainId,
    },
    {
      refetchInterval: 1000,
      enabled: Boolean(marketAddress),
    }
  );

  const { tradeActions, isLoading: isTradeHistoryLoading } = useTradeHistory(chainId, {
    account: null,
    forAllAccounts: true,
    pageSize: TRADE_PAGE_SIZE,
  });

  const recentTrades = useMemo(() => {
    if (!tradeActions) return [];

    return tradeActions
      .filter((action): action is PositionTradeAction => {
        if (!("isLong" in action)) return false;
        const positionAction = action as PositionTradeAction;
        if (positionAction.executionPrice === undefined) return false;
        if (positionAction.eventName !== TradeActionType.OrderExecuted) return false;
        if (positionAction.executionPrice <= 0n) return false;
        if (positionAction.sizeDeltaUsd <= 0n) return false;
        if (marketAddress && positionAction.marketAddress !== marketAddress) return false;
        return true;
      })
      .slice(0, MARKET_TRADE_LIMIT);
  }, [tradeActions, marketAddress]);

  const marketTrades = marketTradesForSelectedMarket.length > 0 ? marketTradesForSelectedMarket : aggregateMarketTrades;
  const showMarketTrades = marketTrades.length > 0;
  const showTradeHistoryFallback = !showMarketTrades && recentTrades.length > 0;
  const isLoading =
    isSelectedMarketTradesLoading ||
    (Boolean(marketAddress) && marketTradesForSelectedMarket.length === 0 && isAggregateMarketTradesLoading) ||
    (!showMarketTrades && isTradeHistoryLoading);
  const marketTradePlaceholderCount = Math.max(0, MARKET_TRADE_LIMIT - marketTrades.length);
  const fallbackTradePlaceholderCount = Math.max(0, MARKET_TRADE_LIMIT - recentTrades.length);

  return (
    <TableScrollFadeContainer className="Trades">
      <table className="w-full">
        <thead>
          <TableTheadTr>
            <TableTh scope="col" className="text-12">
              <Trans>Price</Trans>
            </TableTh>
            <TableTh scope="col" className="text-12">
              <Trans>Size</Trans>
            </TableTh>
            <TableTh scope="col" className="!text-left text-12">
              <Trans>Time</Trans>
            </TableTh>
          </TableTheadTr>
        </thead>
        <tbody>
          {isLoading && <TableListSkeleton Structure={TableTradeStructureSkeleton} count={MARKET_TRADE_LIMIT} />}
          {showMarketTrades &&
            marketTrades.map((trade) => {
              const priceColorClass = trade.side === "buy" ? "Trades-price--buy" : "Trades-price--sell";

              return (
                <TableTr key={trade.id} bordered={false} hoverable={false} className="Trades-row">
                  <TableTd className={cx("text-12", priceColorClass)}>
                    {currencySymbol}
                    {trade.price}
                  </TableTd>
                  <TableTd className="text-12 text-[#7B7B7B]">{trade.sizeInToken}</TableTd>
                  <TableTd className="!text-left text-12 text-[#7B7B7B]">
                    {formatTradeTime(trade.createdAt, "iso")}
                  </TableTd>
                </TableTr>
              );
            })}
          {showMarketTrades &&
            Array.from({ length: marketTradePlaceholderCount }, (_, index) => (
              <TableTr
                key={`market-placeholder-${index}`}
                bordered={false}
                hoverable={false}
                className="Trades-row Trades-row--placeholder"
              >
                <TableTd className="text-12">&nbsp;</TableTd>
                <TableTd className="text-12">&nbsp;</TableTd>
                <TableTd className="!text-left text-12">&nbsp;</TableTd>
              </TableTr>
            ))}
          {!showMarketTrades &&
            showTradeHistoryFallback &&
            recentTrades.map((trade) => {
              const side = getTradeHistorySide(trade.isLong, trade.orderType);
              const priceColorClass = side === "buy" ? "Trades-price--buy" : "Trades-price--sell";

              return (
                <TableTr key={trade.id} bordered={false} hoverable={false} className="Trades-row">
                  <TableTd className={cx("text-12", priceColorClass)}>
                    {formatUsd(trade.executionPrice, { displayDecimals: 2 })}
                  </TableTd>
                  <TableTd className="text-12 text-[#7B7B7B]">
                    ${formatAmountHuman(trade.sizeDeltaUsd, USD_DECIMALS)}
                  </TableTd>
                  <TableTd className="!text-left text-12 text-[#7B7B7B]">
                    {formatTradeTime(trade.timestamp, "unix")}
                  </TableTd>
                </TableTr>
              );
            })}
          {!showMarketTrades &&
            showTradeHistoryFallback &&
            Array.from({ length: fallbackTradePlaceholderCount }, (_, index) => (
              <TableTr
                key={`fallback-placeholder-${index}`}
                bordered={false}
                hoverable={false}
                className="Trades-row Trades-row--placeholder"
              >
                <TableTd className="text-12">&nbsp;</TableTd>
                <TableTd className="text-12">&nbsp;</TableTd>
                <TableTd className="!text-left text-12">&nbsp;</TableTd>
              </TableTr>
            ))}
          {/* Simulation disabled: market panel trades now come from live market trades or live execution history fallback. */}
          {!isLoading && !showMarketTrades && !showTradeHistoryFallback && (
            <TableTr hoverable={false} bordered={false} className="h-[64.5px]">
              <TableTd colSpan={7} className="align-top">
                <div className="text-body-medium text-center text-gray-lighter">
                  <Trans>No Data</Trans>
                </div>
              </TableTd>
            </TableTr>
          )}
        </tbody>
      </table>
    </TableScrollFadeContainer>
  );
};

export default Trades;

const TableTradeStructureSkeleton = () => {
  return (
    <TableTr bordered={false} hoverable={false}>
      <TableTd>
        <Skeleton width={60} />
      </TableTd>
      <TableTd>
        <Skeleton width={60} />
      </TableTd>
      <TableTd className="!text-left">
        <Skeleton width={50} />
      </TableTd>
    </TableTr>
  );
};
